import type { Definition } from '~/types'
import imageSizes from '../../constants/imageSizes'
// when the import happens in the server it is processed as a module, and we need to retrieve the .default

export const PartnerType = Object.freeze({
  ALLIANCE: 'ALLIANCE',
  TECH: 'Tech'
})

export const AccreditationStatus = ['VERIFIED_INTEGRATION', 'IMPLEMENTATION_READY', 'APPROVED_PARTNER']

const PagePartners: Definition = {
  contentType: 'PagePartners',
  fields: [
    {
      id: 'hero',
      name: 'Hero',
      type: 'BlockContent',
      imageOptions: { sizes: imageSizes.columns5_34, lazy: false }
    },
    {
      id: 'notificationBar',
      name: 'Notification Bar',
      type: 'BlockNotificationBar'
    },
    {
      id: 'benefits',
      name: 'Benefits',
      type: 'Array',
      subType: 'ModuleUsp'
    },
    {
      id: 'introMedia',
      name: 'Intro media',
      type: ['ModuleVideo', 'ModuleInternalVideo'],
      imageOptions: { sizes: imageSizes.columns12_5269, lazy: true }
    },
    {
      id: 'contentBlock1',
      name: 'Content block 1',
      type: 'BlockContent',
      imageOptions: {
        sizes: {
          small: {
            width: 327,
            height: 245,
            aspectRatio: '4/3'
          },
          medium: {
            width: 312,
            height: 416,
            aspectRatio: '3/4'
          },
          large: {
            width: 576,
            height: 468,
            aspectRatio: '4/3'
          }
        },
        lazy: true
      }
    },
    {
      id: 'logoBar',
      name: 'Logo bar',
      type: 'BlockLogoBar'
    },
    {
      id: 'contentBlock2',
      name: 'Content block 2',
      type: 'BlockGroup',
      imageOptions: {
        lazy: true,
        sizes: {
          small: { width: 327, height: 436, aspectRatio: '3/4' },
          medium: { width: 312, height: 416, aspectRatio: '3/4' },
          large: { width: 368, height: 491, aspectRatio: '3/4' }
        }
      }
    },
    {
      id: 'cta',
      name: 'Call to action',
      type: 'BlockCallToAction'
    },
    {
      id: 'quote',
      name: 'Quote',
      type: 'BlockQuote'
    },
    {
      id: 'benefitsTitle',
      name: 'Benefits Title',
      type: 'Symbol'
    },
    {
      id: 'benefitsDescription',
      name: 'Benefits Description',
      type: 'Symbol'
    }
  ]
}
const PagePartnersMerchant: Definition = {
  contentType: 'PagePartnersMerchant',
  fields: [
    {
      id: 'notificationBar',
      name: 'Notification Bar',
      type: 'BlockNotificationBar'
    },
    {
      id: 'hero',
      name: 'Hero',
      type: 'BlockContent'
    },
    {
      id: 'contentBlock1',
      name: 'Content block 1',
      type: 'BlockGroup',
      imageOptions: {
        sizes: {
          small: { width: 327, height: 436, aspectRatio: '3/4' },
          medium: { width: 312, height: 416, aspectRatio: '3/4' },
          large: { width: 368, height: 491, aspectRatio: '3/4' }
        },
        lazy: true
      }
    },
    {
      id: 'quote',
      name: 'Quote',
      type: 'BlockQuote',
      imageOptions: {
        sizes: {
          small: { width: 327, height: 245, aspectRatio: '4/3' },
          medium: { width: 312, height: 416, aspectRatio: '3/4' },
          large: { width: 576, height: 432, aspectRatio: '4/3' }
        },
        lazy: true
      }
    },
    {
      id: 'logoBar',
      name: 'Logo bar',
      type: 'BlockLogoBar'
    },
    {
      id: 'media',
      name: 'Media',
      type: 'ModuleMedia',
      imageOptions: { sizes: imageSizes.columns5_34, lazy: true }
    },
    {
      id: 'contentBlock2',
      name: 'Content block 2',
      type: 'BlockGroup'
    },
    {
      id: 'cta',
      name: 'Call to action',
      type: 'BlockCallToAction'
    }
  ]
}
const PagePartnersOverview: Definition = {
  contentType: 'PagePartnersOverview',
  fields: [
    {
      id: 'pageTitle',
      name: 'Page title',
      type: 'Symbol'
    },
    {
      id: 'pageSubtitle',
      name: 'Page subtitle',
      type: 'Symbol'
    },
    {
      id: 'pageEyebrow',
      name: 'Page Eyebrow',
      type: 'Symbol'
    },
    {
      id: 'heroImage',
      name: 'Hero image',
      type: 'ModuleMedia',
      imageOptions: { sizes: imageSizes.columns5_43, lazy: false }
    },
    {
      id: 'topPartnersTitle',
      name: 'Top partners description',
      type: 'Symbol'
    },
    {
      id: 'topPartnersDescription',
      name: 'Top partners description',
      type: 'Symbol'
    },
    {
      id: 'topPartnersItems',
      name: 'Top partners items',
      type: 'Array',
      subType: 'PagePartnersDetail',
      validations: [{ size: { min: 0, max: 10 } }],
      imageOptions: {
        recolor: '#ffffff',
        sizes: {
          small: { width: 320, height: 'auto', aspectRatio: null },
          medium: { width: 320, height: 'auto', aspectRatio: null },
          large: { width: 320, height: 'auto', aspectRatio: null }
        }
      }
    },
    {
      id: 'accreditationStatusBlock',
      name: 'Accreditation Status Block',
      type: 'BlockMediaCardGroup'
    },
    {
      id: 'cta',
      name: 'Call to action',
      type: 'BlockCallToAction'
    }
  ]
}

const BlockPartnerMetadata = {
  contentType: 'BlockPartnerMetadata',
  fields: [
    {
      id: 'partnerName',
      name: 'Partner name',
      type: 'Symbol'
    },
    {
      id: 'location',
      name: 'Location',
      type: 'GlobalDataCountry'
    },
    {
      id: 'languages',
      name: 'Languages',
      type: 'Array',
      subType: 'GlobalDataLanguage'
    },
    {
      id: 'url',
      name: 'Url',
      type: 'Symbol'
    },
    {
      id: 'availability',
      name: 'Availability',
      type: 'Array',
      subType: 'GlobalDataCountry'
    },
    {
      id: 'industries',
      name: 'Industries',
      type: 'Array',
      subType: 'GlobalDataIndustry'
    },
    {
      id: 'products',
      name: 'Products',
      type: 'Array',
      subType: 'GlobalDataProduct'
    },
    {
      id: 'partners',
      name: 'partners',
      type: 'Array',
      subType: 'PagePartnersDetail'
    },
    {
      id: 'paymentMethods',
      name: 'PaymentMethods',
      type: 'Array',
      subType: 'PagePaymentMethodsDetail'
    },
    {
      id: 'terminals',
      name: 'Terminals',
      type: 'Array',
      subType: 'PageTerminalsDetail'
    }
  ]
}

const PagePartnersDetail: Definition = {
  contentType: 'PagePartnersDetail',
  fields: [
    {
      id: 'notificationBar',
      name: 'Notification Bar',
      type: 'BlockNotificationBar'
    },
    {
      id: 'partnerName',
      name: 'Partner name',
      type: 'Symbol',
      includeInParent: true
    },
    {
      id: 'partnerDescription',
      name: 'Partner description',
      type: 'Text',
      includeInParent: true
    },
    {
      id: 'partnerLogo',
      name: 'Partner logo',
      type: 'ModuleMedia',
      includeInParent: true,
      imageOptions: {
        recolor: '#ffffff',
        sizes: {
          small: { width: 240, height: 'auto', aspectRatio: null },
          medium: { width: 240, height: 'auto', aspectRatio: null },
          large: { width: 240, height: 'auto', aspectRatio: null }
        }
      }
    },
    {
      id: 'partnerType',
      name: 'Partner type',
      type: 'Symbol',
      localized: false,
      includeInParent: true,
      validations: [{ in: Object.values(PartnerType) }]
    },
    {
      id: 'accreditationStatus',
      name: 'Accreditation Status',
      type: 'Array',
      subType: 'Symbol',
      localized: false,
      includeInParent: true,
      validations: [{ in: AccreditationStatus }]
    },
    {
      id: 'partnerMetadata',
      name: 'Partner metadata',
      lazy: true,
      includeInParent: true,
      type: 'BlockPartnerMetadataLazy'
    },
    {
      id: 'introMedia',
      name: 'Intro media',
      type: 'ModuleMedia',
      imageOptions: {
        sizes: {
          small: { width: 327, height: 184, aspectRatio: '16/9' },
          medium: { width: 672, height: 378, aspectRatio: '16/9' },
          large: { width: 680, height: 382, aspectRatio: '16/9' }
        },
        lazy: true
      }
    },
    {
      id: 'aboutTitle',
      name: 'About title',
      type: 'Symbol'
    },
    {
      id: 'aboutDescription',
      name: 'About description',
      type: 'RichText'
    },
    {
      id: 'quote',
      name: 'Quote',
      type: 'BlockQuote',
      imageOptions: {
        sizes: {
          small: { width: 327, height: 245, aspectRatio: '4/3' },
          medium: { width: 312, height: 416, aspectRatio: '3/4' },
          large: { width: 312, height: 416, aspectRatio: '3/4' }
        },
        lazy: true
      }
    },

    {
      id: 'contentBlockServices',
      name: 'Content block services',
      type: 'BlockGroup'
    },
    {
      id: 'logoBar',
      name: 'Logo bar',
      type: 'BlockLogoBar'
    },
    {
      id: 'mediaVideo',
      name: 'Media video',
      type: ['ModuleVideo', 'ModuleInternalVideo'],
      imageOptions: { sizes: imageSizes.columns12_52, lazy: true }
    },
    {
      id: 'caseStudy',
      name: 'Case study',
      type: 'BlockQuote',
      imageOptions: {
        sizes: {
          small: { width: 327, height: 184, aspectRatio: '16/9' },
          medium: { width: 400, height: 400, aspectRatio: '1/1' },
          large: { width: 400, height: 400, aspectRatio: '1/1' }
        },
        lazy: true
      }
    },
    {
      id: 'contentBlockSteps',
      name: 'Content block steps',
      type: 'BlockGroup'
    },
    {
      id: 'contentBlock',
      name: 'Image + text block',
      type: 'BlockContent'
    },
    {
      id: 'documentation',
      name: 'Documentation',
      type: 'BlockGroup'
    },
    {
      id: 'cta',
      name: 'Call to action',
      type: 'BlockCallToAction'
    }
  ]
}

const PagePartnersDetailContent: Definition = {
  contentType: 'PagePartnersDetailContent',
  isContent: true,
  fields: [
    {
      id: 'partnerName',
      name: 'Partner name',
      type: 'Symbol',
      includeInParent: true
    },
    {
      id: 'partnerDescription',
      name: 'Partner description',
      type: 'Text',
      includeInParent: true
    },
    {
      id: 'partnerLogo',
      name: 'Partner logo',
      type: 'ModuleMedia',
      imageOptions: {
        recolor: '#ffffff',
        sizes: {
          small: { width: 320, height: 'auto' },
          medium: { width: 320, height: 'auto' },
          large: { width: 320, height: 'auto' }
        }
      },
      includeInParent: true
    },
    {
      id: 'partnerType',
      name: 'Partner type',
      type: 'Symbol',
      localized: false,
      includeInParent: true,
      validations: [{ in: Object.values(PartnerType) }]
    },
    {
      id: 'accreditationStatus',
      name: 'Accreditation Status',
      type: 'Array',
      subType: 'Symbol',
      localized: false,
      includeInParent: true,
      validations: [{ in: AccreditationStatus }]
    },
    {
      id: 'partnerMetadata',
      name: 'Partner metadata',
      includeInParent: true,
      type: 'BlockPartnerMetadata'
    }
  ]
}

const PartnersLabels: Definition = {
  contentType: 'PartnersLabels',
  fields: [
    {
      id: 'industry',
      name: 'Industry',
      type: 'Symbol'
    },
    {
      id: 'region',
      name: 'Region',
      type: 'Symbol'
    },
    {
      id: 'product',
      name: 'Product',
      type: 'Symbol'
    },
    {
      id: 'language',
      name: 'Language',
      type: 'Symbol'
    },
    {
      id: 'partnersFound',
      name: 'Partners found',
      type: 'Symbol'
    },
    {
      id: 'all',
      name: 'All',
      type: 'Symbol'
    },
    {
      id: 'alliancePartner',
      name: 'Alliance partner',
      type: 'Symbol'
    },
    {
      id: 'alliancePartners',
      name: 'Alliance partners',
      type: 'Symbol'
    },
    {
      id: 'techPartner',
      name: 'Tech partner',
      type: 'Symbol'
    },
    {
      id: 'techPartners',
      name: 'Tech partners',
      type: 'Symbol'
    },
    {
      id: 'showAllPartners',
      name: 'Show all partners',
      type: 'Symbol'
    },
    {
      id: 'sorryMessage',
      name: 'Sorry message',
      type: 'Symbol'
    },
    {
      id: 'showAll',
      name: 'Show all',
      type: 'Symbol'
    },
    {
      id: 'availableIn',
      name: 'Available in',
      type: 'Symbol'
    },
    {
      id: 'terminalsSupported',
      name: 'Terminals supported',
      type: 'Symbol'
    },
    {
      id: 'paymentMethods',
      name: 'Payment methods',
      type: 'Symbol'
    },
    {
      id: 'adyenProduct',
      name: 'Adyen product',
      type: 'Symbol'
    }
  ]
}

export default {
  PagePartners,
  PagePartnersMerchant,
  PagePartnersOverview,
  PartnersLabels,
  PagePartnersDetail,
  PagePartnersDetailContent,
  BlockPartnerMetadata,
  PartnerType
}
